// Pure, dependency-free template rendering shared by the campaign worker, the
// wizard preview, keywords, and invites (Section 6.1).

export type TemplateVars = {
  location_name: string;
  promo_title: string;
  offer_link: string;
  first_name: string;
  store_phone: string;
  store_address: string;
  compliance_footer: string;
  invite_footer: string;
};

export const SUPPORTED_VARIABLES: readonly string[] = [
  "location_name",
  "promo_title",
  "offer_link",
  "first_name",
  "store_phone",
  "store_address",
  "compliance_footer",
  "invite_footer",
];

/**
 * Substitutes `{variable}` placeholders. An unknown placeholder is left
 * as-is (not blanked) so a typo in a template is visible rather than
 * silently disappearing. A missing known variable resolves to '' and any
 * doubled space or space-before-comma that leaves behind is collapsed, so
 * "Hi {first_name}, deal" with no first name reads "Hi, deal" rather than
 * "Hi , deal".
 */
export function renderTemplate(body: string, vars: Partial<TemplateVars>): string {
  const substituted = body.replace(/\{(\w+)\}/g, (match, key: string) => {
    if (!SUPPORTED_VARIABLES.includes(key)) return match;
    return vars[key as keyof TemplateVars] ?? "";
  });
  return substituted.replace(/ +,/g, ",").replace(/ {2,}/g, " ");
}

/** Every `{variable}` placeholder referenced in `body`, unique, in order. */
export function listVariables(body: string): string[] {
  const found: string[] = [];
  const seen = new Set<string>();
  const regex = /\{(\w+)\}/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(body)) !== null) {
    if (!seen.has(match[1])) {
      seen.add(match[1]);
      found.push(match[1]);
    }
  }
  return found;
}
